import React, { useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { IconButton, Collapse } from "@mui/material";

import { UIIcon, UICheckbox, UIButton, UIModal } from "../ui";
import { clx } from "../../utils/Helpers";
import "./DemoRequestsTable.scss";

const DEMO_REQUEST_CONTACTED = "contacted";
const DEMO_REQUEST_PENDING = "pending";

const DemoRequestsTable = ({
  rows,
  labels,
  expandedRowId,
  onToggleRow,
  onToggleContacted,
  onDelete,
}) => {
  const [removeTarget, setRemoveTarget] = useState(null);

  const handleConfirmRemove = () => {
    if (removeTarget) {
      onDelete(removeTarget.id, removeTarget.name);
    }
    setRemoveTarget(null);
  };

  return (
  <div className="demo-requests-table" role="table">
    <div
      className="demo-requests-table__row demo-requests-table__row--header"
      role="row"
    >
      <span className="demo-requests-table__cell demo-requests-table__cell--toggle">
        {labels.details}
      </span>
      <span className="demo-requests-table__cell">{labels.name}</span>
      <span className="demo-requests-table__cell">{labels.workEmail}</span>
      <span className="demo-requests-table__cell">{labels.phoneNumber}</span>
      <span className="demo-requests-table__cell">{labels.companyName}</span>
      <span className="demo-requests-table__cell">{labels.dateRequested}</span>
      <span className="demo-requests-table__cell demo-requests-table__cell--center">
        {labels.contacted}
      </span>
      <span className="demo-requests-table__cell demo-requests-table__cell--center">
        {labels.actions}
      </span>
    </div>

    {rows.map((row) => {
      const isExpanded = expandedRowId === row.id;
      const isContacted = row.status === DEMO_REQUEST_CONTACTED;

      return (
        <div
          key={row.id}
          className={clx(
            "demo-requests-table__group",
            isExpanded && "demo-requests-table__group--expanded",
          )}
        >
          <div className="demo-requests-table__row" role="row">
            <span className="demo-requests-table__cell demo-requests-table__cell--toggle">
              <IconButton
                className="demo-requests-table__toggle-btn"
                onClick={() => onToggleRow(row.id)}
                aria-label={isExpanded ? labels.collapse : labels.expand}
                aria-expanded={isExpanded}
                size="small"
              >
                <UIIcon
                  name="chevronDown"
                  size="sm"
                  className={clx(
                    "demo-requests-table__chevron",
                    isExpanded && "demo-requests-table__chevron--up",
                  )}
                />
              </IconButton>
            </span>
            <span className="demo-requests-table__cell" title={row.name}>
              {row.name}
            </span>
            <span className="demo-requests-table__cell" title={row.email}>
              {row.email}
            </span>
            <span className="demo-requests-table__cell">
              {row.phone_number}
            </span>
            <span
              className="demo-requests-table__cell"
              title={row.company_name}
            >
              {row.company_name}
            </span>
            <span className="demo-requests-table__cell">
              {row.created_at
                ? dayjs(row.created_at).format("DD MMM YYYY")
                : "-"}
            </span>
            <span className="demo-requests-table__cell demo-requests-table__cell--center">
              <UICheckbox
                name={`contacted-${row.id}`}
                label=""
                checked={isContacted}
                onChange={(checked) =>
                  onToggleContacted(
                    row.id,
                    checked ? DEMO_REQUEST_CONTACTED : DEMO_REQUEST_PENDING,
                  )
                }
              />
            </span>
            <span className="demo-requests-table__cell demo-requests-table__cell--center">
              <UIButton
                variant="destructive"
                appearance="outline"
                size="small"
                className="demo-requests-table__remove-btn"
                onClick={() => setRemoveTarget(row)}
              >
                {labels.remove}
              </UIButton>
            </span>
          </div>

          <Collapse in={isExpanded} timeout={260}>
            <div className="demo-requests-table__details">
              <p className="demo-requests-table__details-label">
                {labels.requestDetailsLabel}
              </p>
              <p className="demo-requests-table__details-text">
                {row.request_details || "-"}
              </p>
            </div>
          </Collapse>
        </div>
      );
    })}

    <UIModal
      open={Boolean(removeTarget)}
      type="destructive"
      title={labels.removeTitle}
      description={
        removeTarget
          ? labels.removeDescription.replace("{name}", removeTarget.name || "")
          : ""
      }
      cancelLabel={labels.cancel}
      confirmLabel={labels.removeAction}
      onCancel={() => setRemoveTarget(null)}
      onClose={() => setRemoveTarget(null)}
      onConfirm={handleConfirmRemove}
    />
  </div>
  );
};

DemoRequestsTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
      phone_number: PropTypes.string,
      company_name: PropTypes.string,
      created_at: PropTypes.string,
      status: PropTypes.string,
      request_details: PropTypes.string,
    }),
  ).isRequired,
  labels: PropTypes.object.isRequired,
  expandedRowId: PropTypes.number,
  onToggleRow: PropTypes.func.isRequired,
  onToggleContacted: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

DemoRequestsTable.defaultProps = {
  expandedRowId: null,
};

export default DemoRequestsTable;
